import { Injectable } from '@angular/core';

import { CheckinMeta, CheckinReasonType, EditableCheckin, ICheckinReason, ICheckinReasonWithCounts } from 'libs/domain';
import { RestrictionErrorMinimal } from 'libs/domain';
import { checkin_ng_grpc_pb, checkin_pb } from 'libs/generated-grpc-web';
import { CheckinReasonMapper, CheckinsMapper } from 'libs/shared-grpc';
import { RestrictionErrorMapper } from 'libs/shared-grpc';
import { from, Observable } from 'rxjs';

import { RootService } from '@app/src/app/minimal/services/RootService';
import { PeopleSelectorService } from '@app/src/app/modules/people-selector';
import {
  handleCheckInError,
  showCheckInSuccessDialog,
} from '@app/src/app/modules/tools/teacher-tools/components/tt-my-class/utils/check-in.utils';

import { CHECKIN_ICONS } from '@modules/checkin-manager/components';

export type PersonDetails = { name: string; hash: string };

export type CheckinResponseDetails = {
  successPeopleHashes: string[];
  errors: RestrictionErrorMinimal[];
};

/**
 * Checkin Service
 */
@Injectable({ providedIn: 'root' })
export class CheckinService {
  /** Constructor */
  constructor(
    private _checkInManager: checkin_ng_grpc_pb.CheckinManager,
    private _rootService: RootService,
    private _peopleSelector: PeopleSelectorService,
  ) {}

  /**
   * Get Reasons
   */
  public getReasonsObs(getActiveOnly: boolean = true): Observable<ICheckinReason[]> {
    return from(this.getReasons(getActiveOnly));
  }

  /**
   * Handle Get Reasons
   *
   * This is the actual request to fetch reasons
   *
   * @todo fix hardcoded offset and limit
   */
  public async getReasons(
    getActiveOnly: boolean = true,
    includeCounts: boolean = false,
    getKioskOnly: boolean = false,
  ): Promise<ICheckinReasonWithCounts[]> {
    const req = new checkin_pb.GetMingaCheckinReasonsRequest();
    req.setGetActiveOnly(getActiveOnly);
    req.setKioskOnly(getKioskOnly);
    req.setOffset(0);
    req.setLimit(1000);
    req.setIncludeCounts(includeCounts);

    const res = await this._checkInManager.getMingaCheckinReasons(req);

    return res.getCheckinReasonsList().map(CheckinReasonMapper.fromProto);
  }

  /**
   * Get Reason
   *
   * Gets a single reason by ID, kept seperate from getReasons to keep methods
   * predictable and simple.
   */
  public getReasonObs(id: number): Observable<ICheckinReason> {
    const req = new checkin_pb.GetMingaCheckinReasonRequest();

    req.setId(id);

    return from(this._handleGetReason(req));
  }

  public getReason(id: number): Promise<ICheckinReason> {
    const req = new checkin_pb.GetMingaCheckinReasonRequest();

    req.setId(id);

    return this._handleGetReason(req);
  }

  private async _handleGetReason(req: checkin_pb.GetMingaCheckinReasonRequest): Promise<ICheckinReason> {
    const res = await this._checkInManager.getMingaCheckinReason(req);

    const reason = res.getCheckinReason();

    return CheckinReasonMapper.fromProto(reason);
  }

  /**
   * Create Type With Placeholder
   *
   * Creates a new type with placeholder values
   */
  public async createReasonWithPlaceholder(): Promise<ICheckinReason> {
    return await this.updateReason({
      name: '',
      mingaId: 0,
      active: false,
      pointReward: 0,
      icon: CHECKIN_ICONS[0].value,
      showAbsentees: false,
      selfCheckIn: false,
      stickerIds: [],
      checkinReasonType: CheckinReasonType.CHECKIN,
    });
  }

  /**
   * Update Type
   */
  public async updateReason(type: ICheckinReason, bypassRestrictions?: boolean): Promise<ICheckinReason> {
    const req = new checkin_pb.UpsertCheckinReasonRequest();

    req.setCheckinReason(CheckinReasonMapper.toProto(type));
    if (bypassRestrictions) req.setBypassRestrictions(bypassRestrictions);

    const res = await this._checkInManager.upsertCheckinReason(req);

    return CheckinReasonMapper.fromProto(res.getCheckinReason());
  }

  public async toggleCheckinReasonActive(reason: ICheckinReason) {
    const req = new checkin_pb.ToggleCheckinReasonActiveRequest();
    req.setCheckinReasonId(reason.id);

    await this._checkInManager.toggleCheckinReasonActive(req);
  }

  /**
   * Delete Reason
   */
  public async deleteReason(id: number): Promise<void> {
    const req = new checkin_pb.DeleteCheckinReasonRequest();

    req.setCheckinReasonId(id);

    await this._checkInManager.deleteCheckinReason(req);
  }

  /**
   * Add Default Checkin Reasons To Minga
   */
  public async addDefaultCheckinReasonsToMinga(mingaHash: string) {
    const req = new checkin_pb.AddDefaultCheckinReasonsRequest();

    req.setMingaHash(mingaHash);

    const res = await this._checkInManager.addDefaultCheckinReasonsToMinga(req);
    const err = res.getErrorMessage();

    return err;
  }

  /**
   * Create Checkin
   */
  public async createCheckin(
    personHashes: string[],
    reasonId: string | number,
    opts?: {
      note?: string;
      bypassMultiCheckin?: boolean;
      createdViaKiosk?: boolean;
      meta?: CheckinMeta;
      listId?: number;
    },
  ): Promise<CheckinResponseDetails> {
    const { note, createdViaKiosk, bypassMultiCheckin, meta, listId } = opts || {};
    try {
      const req = new checkin_pb.CheckinRequest();
      req.setPeopleHashesList(personHashes);
      req.setCheckinReasonId(reasonId as number);
      req.setBypassMultiCheck(bypassMultiCheckin ?? false);
      req.setNote(note);
      req.setIsKiosk(createdViaKiosk ?? false);
      req.setListId(listId);

      if (meta) {
        const metaMap = req.getMetaMap();
        const metaMsg = new checkin_pb.Meta();
        if (meta.isTardy !== undefined) {
          metaMsg.setIsTardy(meta.isTardy);
        }
        if (meta.forceCheckIn !== undefined) {
          metaMsg.setForceCheckIn(meta.forceCheckIn);
        }
        metaMap.set('meta', metaMsg);
      }

      const response = await this._rootService.addLoadingPromise<checkin_pb.CheckinResponse>(
        this._checkInManager.checkin(req),
      );
      return {
        errors: response.getErrorList().map(RestrictionErrorMapper.fromProto),
        successPeopleHashes: response.getSuccessPeopleHashesList(),
      };
    } catch (e) {
      throw new Error(e.message);
    }
  }

  /**
   * Create Checkout
   */
  public async createCheckout(personHashes: string[], reasonId: string | number, note?: string) {
    try {
      const req = new checkin_pb.CheckoutRequest();

      req.setPeopleHashesList(personHashes);
      req.setCheckinReasonId(reasonId as number);

      const response = await this._rootService.addLoadingPromise<checkin_pb.CheckoutResponse>(
        this._checkInManager.checkout(req),
      );

      const errors = response.getErrorList().map(RestrictionErrorMapper.fromProto);

      return {
        errors,
        successPeopleHashes: response.getSuccessPeopleHashesList(),
      };
    } catch (e) {
      throw new Error(e.message);
    }
  }

  public async updateCheckin(checkin: EditableCheckin) {
    const req = new checkin_pb.UpdateCheckinRequest();
    req.setCheckin(CheckinsMapper.toProtoEditable(checkin));

    const response = await this._checkInManager.updateCheckin(req);
    checkin = CheckinsMapper.fromProtoEditable(response.getCheckin());
    return checkin;
  }

  /**
   *  Helper to create a check in with success and error dialogs flows
   * @param students collection of initial students attempting to check in
   * @param reason
   * @param opts
   * * @param opts.note optional note to add to the check in
   * * @param opts.bypassMultiCheckin if true, will bypass multi check in
   * * @param opts.createdViaKiosk if true, will mark the check in as created via kiosk
   * * @param opts.meta optional meta data to add to the check in
   * * @param opts.showProfilePicture if true, will show the profile picture in the success dialog
   * * @param opts.canBypassError Whether user can bypass the error and check in anyway
   * @returns
   */
  public async createCheckinWithDialogs(
    students: Array<{
      personHash: string;
      displayName: string;
      profileImageUrl?: string;
    }>,
    reason: {
      id: string | number;
      name: string;
    },
    opts?: {
      note?: string;
      bypassMultiCheckin?: boolean;
      createdViaKiosk?: boolean;
      meta?: CheckinMeta;
      showProfilePicture?: boolean;
      canBypassError?: boolean;
      listId?: number;
    },
  ): Promise<{
    successHashes: string[];
  }> {
    const options = opts || {};
    const meta = options.meta || {};
    const { showProfilePicture, canBypassError = false } = options;
    const hashes = students.map(s => s.personHash);
    const successHashes: string[] = [];
    const showSuccess = async (peopleHashes: string[]) => {
      await showCheckInSuccessDialog(
        students.filter(s => peopleHashes.includes(s.personHash)),
        this._peopleSelector.openDialog.bind(this._peopleSelector),
        {
          showProfilePicture,
          message: `${meta.isTardy ? 'Tardy - ' : ''}${reason.name}`,
          checkType: 'in',
        },
      );
    };

    const { errors, successPeopleHashes } = await this.createCheckin(hashes, reason.id, options);
    successHashes.push(...successPeopleHashes);

    if (errors.length > 0) {
      const bypassPeople = await handleCheckInError(students, successPeopleHashes, {
        errors,
        reasonName: reason.name,
        showProfilePicture,
        openDialogHandler: this._peopleSelector.openDialog.bind(this._peopleSelector),
        summaryDialogHandler: this._peopleSelector.openSummary.bind(this._peopleSelector),
        canBypassError,
      });

      if (bypassPeople.length) {
        const { errors: bypassErrors, successPeopleHashes: bypassSuccess } = await this.createCheckin(
          bypassPeople,
          reason.id,
          {
            ...options,
            bypassMultiCheckin: true,
            meta: {
              ...meta,
              forceCheckIn: true,
            },
          },
        );

        successHashes.push(...bypassSuccess);

        if (bypassSuccess.length > 0 && bypassErrors.length === 0) {
          await showSuccess(bypassSuccess);
        } else {
          // we shouldn't reach here
          throw new Error('Could not bypass check in restrictions');
        }
      }
      return {
        successHashes,
      };
    }

    // handle happy path no errors
    await showSuccess(successPeopleHashes);
    return {
      successHashes,
    };
  }
}
